import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Ho4ArticleSidebar from '../components/Ho4ArticleSidebar';
import { HO4_LAST_MODIFIED } from '../data/ho4Articles';

function Ho4ProofOfInsuranceArticlePage() {
  const navigate = useNavigate();
  const articleLinkStyle = { color: '#065F46', fontWeight: 600, textDecoration: 'underline' };

  useEffect(() => {
    const previousTitle = document.title;
    const descriptionMeta = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionMeta?.getAttribute('content') ?? '';

    document.title = 'How to Get Proof of Renters Insurance for Your Landlord | GetSkimp';

    if (descriptionMeta) {
      descriptionMeta.setAttribute(
        'content',
        'Step-by-step guide to getting proof of renters insurance for your landlord, including declarations pages, interested party requests, and common lease requirements.'
      );
    }

    return () => {
      document.title = previousTitle;
      if (descriptionMeta) descriptionMeta.setAttribute('content', previousDescription);
    };
  }, []);

  return (
    <div className="container">
      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: '2rem' }}>
        <Ho4ArticleSidebar
          activeHref="/knowledge-base/how-to-get-proof-of-renters-insurance-for-landlord"
          onNavigate={(href) => navigate(href)}
        />
        <section>
          <div style={{ marginBottom: '1rem' }}>
            <button type="button" className="btn btn-sm btn-outline" onClick={() => navigate('/knowledge-base')} style={{ marginBottom: '1rem' }}>
              Back to self help library
            </button>
            <h2 style={{ marginBottom: '0.5rem' }}>How to get proof of renters insurance for your landlord</h2>
            <p style={{ marginBottom: 0, color: '#4B5563' }}>
              Most landlords and property managers require proof of active HO-4 coverage before move-in. Here is how to
              get the right document fast. See also{' '}
              <Link to="/knowledge-base/is-ho4-required-by-landlord" style={articleLinkStyle}>when landlords require renters insurance</Link>.
            </p>
            <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#6B7280' }}>Last modified: {HO4_LAST_MODIFIED}</p>
          </div>

          <article className="card mb-4">
            <div className="card-body">
              <h3 style={{ marginBottom: '0.75rem' }}>Quick answer</h3>
              <p>
                After you purchase a renters insurance policy, your insurer provides a <strong>declarations page</strong>{' '}
                or <strong>certificate of insurance</strong> as proof. You typically need to add your landlord or property
                management company as an <strong>interested party</strong> (also called additional interest) so they receive
                direct confirmation that your policy is active.
              </p>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>What landlords usually require</h3>
              <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>Active policy with a start date on or before your lease start or move-in date.</li>
                <li style={{ marginBottom: '0.5rem' }}>
                  Minimum personal liability limit — often $100,000 or $300,000. See{' '}
                  <Link to="/knowledge-base/how-much-liability-do-i-need-for-renters-insurance" style={articleLinkStyle}>how much liability you may need</Link>.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>Landlord or management company listed as interested party / additional interest.</li>
                <li>Proof submitted through their portal, email, or leasing office before key pickup.</li>
              </ul>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Step-by-step: get proof of insurance</h3>
              <ol style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>Buy your HO-4 policy</strong> — follow{' '}
                  <Link to="/knowledge-base/how-to-get-ho4-renters-insurance" style={articleLinkStyle}>how to get HO-4 renters insurance</Link>{' '}
                  and set your coverage start date to meet your lease deadline.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>Add your landlord as interested party</strong> — provide the property management company name
                  and mailing address exactly as your lease specifies.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>Download your declarations page</strong> — available from your insurer&apos;s app, website, or
                  agent. This shows your name, address, coverage limits, and effective dates.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>Submit proof through your landlord&apos;s process</strong> — many apartment portals (AppFolio,
                  Yardi, etc.) accept uploads directly.
                </li>
                <li>
                  <strong>Keep a copy</strong> and set a renewal reminder so coverage does not lapse mid-lease.
                </li>
              </ol>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Declarations page vs. certificate of insurance</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Document</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>What it shows</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>When you need it</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Declarations page</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Your full policy summary — limits, deductibles, dates</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Most common proof for apartment leases</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '0.6rem' }}>Certificate of insurance (COI)</td>
                      <td style={{ padding: '0.6rem' }}>Formal summary sent to interested parties</td>
                      <td style={{ padding: '0.6rem' }}>When landlord requires direct insurer notification</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Common mistakes that delay move-in</h3>
              <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>Coverage start date after your lease begins.</li>
                <li style={{ marginBottom: '0.5rem' }}>Liability limit below the lease minimum.</li>
                <li style={{ marginBottom: '0.5rem' }}>Wrong rental address on the policy.</li>
                <li style={{ marginBottom: '0.5rem' }}>Landlord not listed as interested party when the lease requires it.</li>
                <li>Submitting a quote instead of an active, bound policy.</li>
              </ul>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Need same-day proof?</h3>
              <p style={{ marginBottom: 0 }}>
                Many insurers can bind a policy and issue proof the same day you apply. If you are switching carriers,
                see{' '}
                <Link to="/knowledge-base/how-to-switch-ho4-insurance-companies" style={articleLinkStyle}>how to switch HO-4 companies</Link>{' '}
                to avoid a coverage gap that could delay landlord approval.
              </p>
            </div>
          </article>

          <section className="card mb-4" style={{ borderColor: 'var(--color-green)', background: 'linear-gradient(135deg, #ECFDF5 0%, #FFFFFF 100%)', boxShadow: 'var(--shadow-md)' }}>
            <div className="card-body">
              <h3 style={{ marginBottom: '0.5rem', color: '#065F46' }}>Start renters insurance with GetSkimp</h3>
              <p style={{ marginBottom: '1rem' }}>Get HO-4 coverage and proof of insurance that meets your lease requirements.</p>
              <button type="button" className="btn btn-primary" onClick={() => navigate('/qualify')} style={{ fontWeight: 700, paddingInline: '1.4rem' }}>Get started now</button>
            </div>
          </section>
          <section className="card" style={{ borderColor: '#FBBF24', background: 'linear-gradient(135deg, #FFFBEB 0%, #FFFFFF 100%)' }}>
            <div className="card-body">
              <h3 style={{ marginBottom: '0.5rem', color: '#92400E' }}>Need help meeting a lease deadline?</h3>
              <p style={{ marginBottom: '1rem' }}>An agent can help you bind coverage and get proof to your landlord quickly.</p>
              <button type="button" className="btn btn-outline" onClick={() => navigate('/help')} style={{ borderColor: '#D97706', color: '#92400E', fontWeight: 700 }}>Speak to an agent</button>
            </div>
          </section>
        </section>
      </div>
    </div>
  );
}

export default Ho4ProofOfInsuranceArticlePage;
